import { PixelService } from './service';
import { env } from '$lib/config/environment';
import { Log } from '@ainova/core';

declare global {
  /**
   * The Meta pixel's global.
   *
   * `fbq` is a function that also carries the bookkeeping the loader snippet
   * hangs on it — the queue of calls made before the script arrives, and the
   * dispatcher it installs. Declared as a bare function, assigning any of
   * those was an error.
   */
  interface Window {
    fbq: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq: Window['fbq'];
  }
}

/**
 * Meta Pixel (Facebook Pixel) implementation
 */
export class PixelServiceMeta extends PixelService {
  private logger: Log;
  private pixelId: string;
  private initialized = false;
  private scriptLoadPromise: Promise<void> | null = null;

  constructor() {
    super();
    this.logger = new Log('MetaPixel');
    this.pixelId = env.metaPixel.pixelId;

    if (!env.metaPixel.enabled) {
      this.logger.info('Meta Pixel disabled');
      return;
    }

    if (!this.pixelId) {
      this.logger.warn('Meta Pixel ID not configured');
      return;
    }

    if (typeof window !== 'undefined') {
      this.initialize();
    }
  }

  private initialize(): void {
    try {
      // Initialize the fbq function
      if (!window.fbq) {
        window.fbq = function (...args: unknown[]) {
          if (window.fbq.callMethod) {
            window.fbq.callMethod(...args);
          } else {
            window.fbq.queue?.push(args);
          }
        };

        if (!window._fbq) {
          window._fbq = window.fbq;
        }

        window.fbq.push = window.fbq;
        window.fbq.loaded = true;
        window.fbq.version = '2.0';
        window.fbq.queue = [];
      }

      // Load the pixel script
      this.loadScript();

      // Initialize the pixel
      window.fbq('init', this.pixelId);

      this.initialized = true;
      this.logger.info('Meta Pixel initialized', { pixelId: this.pixelId });
    } catch (error) {
      this.logger.error('Failed to initialize Meta Pixel', error);
    }
  }

  private loadScript(): void {
    if (this.scriptLoadPromise) {
      return;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      try {
        // Check if script already exists
        const existingScript = document.querySelector('script[src*="connect.facebook.net"]');
        if (existingScript) {
          this.logger.info('Meta Pixel script already loaded');
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        script.async = true;

        script.onload = () => {
          this.logger.info('Meta Pixel script loaded');
          resolve();
        };

        script.onerror = (error) => {
          this.logger.error('Failed to load Meta Pixel script', error);
          reject(new Error('Failed to load Meta Pixel script'));
        };

        document.head.appendChild(script);
      } catch (error) {
        this.logger.error('Error setting up Meta Pixel script loader', error);
        reject(error);
      }
    });
  }

  async trackConversion(eventName: string, params?: Record<string, unknown>): Promise<void> {
    if (!this.initialized || !env.metaPixel.enabled) {
      return;
    }

    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', eventName, params || {});
        this.logger.debug(`Conversion tracked: ${eventName}`, params);
      }
    } catch (error) {
      this.logger.error(`Failed to track conversion: ${eventName}`, error);
    }
  }

  async trackEvent(eventName: string, params?: Record<string, unknown>): Promise<void> {
    if (!this.initialized || !env.metaPixel.enabled) {
      return;
    }

    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', eventName, params || {});
        this.logger.debug(`Event tracked: ${eventName}`, params);
      }
    } catch (error) {
      this.logger.error(`Failed to track event: ${eventName}`, error);
    }
  }

  async pageView(path: string): Promise<void> {
    if (!this.initialized || !env.metaPixel.enabled) {
      return;
    }

    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PageView');
        this.logger.debug(`Page view tracked: ${path}`);
      }
    } catch (error) {
      this.logger.error('Failed to track page view', error);
    }
  }
}
